/**
 * Anomaly Model Health Tab - Admin Console
 * Requirement 37.2/39.3: shows when the L2/L3 ensemble model for each
 * provider/service was last (re)trained and why - real, persisted state
 * from anomaly_model_state, not a status light with nothing behind it.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getActiveTenantId } from '../../services/tenantContext';

const OPTIMIZATION_API_URL = import.meta.env.VITE_OPTIMIZATION_API_URL || 'http://localhost:3005';

function getHeaders(): Record<string, string> {
  const token = localStorage.getItem('accessToken');
  return {
    'x-tenant-id': getActiveTenantId(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

interface ModelHealthRow {
  provider: string;
  service: string;
  trained_at: string;
  training_window_days: number;
  last_retrain_reason: string | null;
}

interface HallucinationIncident {
  claim: string;
  evidence_match_score: string;
  flagged_at: string;
  anomaly_id: string;
}

interface PrecisionMetrics {
  window: string;
  reviewed_count: number;
  confirmed_count: number;
  false_positive_count: number;
  precision_percent: number | null;
  note?: string;
}

interface RcaAccuracyMetrics {
  labeled_count: number;
  confirmed_count: number;
  accuracy_percent: number | null;
}

interface ReconciliationMetrics {
  total_count: number;
  within_tolerance_count: number;
  within_tolerance_percent: number | null;
  avg_deviation_percent: number | null;
}

interface UsefulnessMetrics {
  rated_count: number;
  useful_count: number;
  useful_percent: number | null;
  avg_rating: number | null;
}

export const AnomalyModelHealthTab: React.FC = () => {
  const [rows, setRows] = useState<ModelHealthRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [incidents30d, setIncidents30d] = useState<number | null>(null);
  const [recentIncidents, setRecentIncidents] = useState<HallucinationIncident[]>([]);
  const [precision, setPrecision] = useState<PrecisionMetrics | null>(null);
  const [rcaAccuracy, setRcaAccuracy] = useState<RcaAccuracyMetrics | null>(null);
  const [reconciliation, setReconciliation] = useState<ReconciliationMetrics | null>(null);
  const [usefulness, setUsefulness] = useState<UsefulnessMetrics | null>(null);

  useEffect(() => {
    const fetchHealth = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${OPTIMIZATION_API_URL}/api/v1/anomalies/model-health`, { headers: getHeaders() });
        setRows(res.data?.data || []);
        setError(null);
      } catch (e: any) {
        setError(e.message || 'Failed to load model health');
      } finally {
        setLoading(false);
      }
    };
    const fetchHallucinationMetrics = async () => {
      try {
        const res = await axios.get(`${OPTIMIZATION_API_URL}/admin/hallucination-metrics`, { headers: getHeaders() });
        setIncidents30d(res.data?.data?.incidents_30d ?? null);
        setRecentIncidents(res.data?.data?.recent || []);
      } catch (e: any) {
        // Best-effort card - a failure here shouldn't block the model-health table above.
        console.warn('Could not load hallucination metrics:', e.message);
      }
    };
    // Requirement 37.6 - a real precision figure computed from actual human
    // labels (acknowledged/resolved vs. false_positive), not a fabricated
    // number. See /admin/anomaly-precision-metrics for the honest scope
    // note on why this is precision only, not recall.
    const fetchPrecisionMetrics = async () => {
      try {
        const res = await axios.get(`${OPTIMIZATION_API_URL}/admin/anomaly-precision-metrics`, { headers: getHeaders() });
        setPrecision(res.data?.data || null);
      } catch (e: any) {
        console.warn('Could not load anomaly precision metrics:', e.message);
      }
    };
    // Requirement 41.5 - real RCA accuracy from analyst-confirmed causes
    // (see RCAAnalysisTab.tsx's "Was this correct?" control).
    const fetchRcaAccuracy = async () => {
      try {
        const res = await axios.get(`${OPTIMIZATION_API_URL}/admin/rca-accuracy-metrics`, { headers: getHeaders() });
        setRcaAccuracy(res.data?.data || null);
      } catch (e: any) {
        console.warn('Could not load RCA accuracy metrics:', e.message);
      }
    };
    // AC-RCA-002 - the ±3% reconciliation tolerance, rolled up across
    // recent RCA runs (previously computed per-call and never aggregated).
    const fetchReconciliation = async () => {
      try {
        const res = await axios.get(`${OPTIMIZATION_API_URL}/admin/rca-reconciliation-metrics`, { headers: getHeaders() });
        setReconciliation(res.data?.data || null);
      } catch (e: any) {
        console.warn('Could not load RCA reconciliation metrics:', e.message);
      }
    };
    // AC-RCA-003 - real analyst "was this useful" feedback (RCAAnalysisTab's
    // footer thumbs up/down), rolled up.
    const fetchUsefulness = async () => {
      try {
        const res = await axios.get(`${OPTIMIZATION_API_URL}/admin/rca-usefulness-metrics`, { headers: getHeaders() });
        setUsefulness(res.data?.data || null);
      } catch (e: any) {
        console.warn('Could not load RCA usefulness metrics:', e.message);
      }
    };
    fetchHealth();
    fetchHallucinationMetrics();
    fetchPrecisionMetrics();
    fetchRcaAccuracy();
    fetchReconciliation();
    fetchUsefulness();
  }, []);

  const hoursSince = (iso: string) => Math.round((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60));

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card">
        <h3 className="mb-1.5 text-sm font-semibold text-foreground">Anomaly Model Health</h3>
        <p className="mb-6 text-sm text-muted-foreground">
          The L2 (seasonal) and L3 (multi-feature) ensemble models are reused across hourly detection runs instead of
          retraining from scratch every time, refreshed every 24h or sooner if real distribution drift (PSI) is
          detected.
        </p>

        {error && (
          <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-800 dark:border-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No trained models yet - the hourly detection cron will populate this after its next run.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Provider</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Service</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Last Trained</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Window</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border dark:divide-border">
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 text-sm text-foreground uppercase">{r.provider}</td>
                    <td className="px-3 py-2 text-sm text-foreground">{r.service}</td>
                    <td className="px-3 py-2 text-sm text-muted-foreground">
                      {new Date(r.trained_at).toLocaleString()} ({hoursSince(r.trained_at)}h ago)
                    </td>
                    <td className="px-3 py-2 text-sm text-muted-foreground">{r.training_window_days}d</td>
                    <td className="px-3 py-2 text-sm text-muted-foreground">{r.last_retrain_reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card">
        <h3 className="mb-1.5 text-sm font-semibold text-foreground">Detection Precision (Req 37.6)</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          Computed from real human labels: an anomaly acknowledged or resolved counts as confirmed real, one marked
          false-positive counts against precision. Precision only, not recall - recall would need an independent
          ground-truth feed of anomalies the detector never surfaced at all, which doesn't exist.
        </p>
        {precision && precision.reviewed_count > 0 ? (
          <div className="flex items-baseline gap-4">
            <div className="text-base font-semibold tracking-tight text-foreground">
              {precision.precision_percent}% <span className="text-sm font-normal text-muted-foreground">precision (30d)</span>
            </div>
            <div className="text-sm text-muted-foreground">
              {precision.confirmed_count} confirmed / {precision.false_positive_count} false-positive ({precision.reviewed_count} reviewed)
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{precision?.note || 'Loading...'}</p>
        )}
      </div>

      <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card">
        <h3 className="mb-1.5 text-sm font-semibold text-foreground">RCA Accuracy (Req 41.5)</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          Real analyst confirmations of the primary (rank 1) cause an RCA narrative asserts - recorded via the "Was
          this correct?" control on each anomaly's RCA tab. The spec's own bar is &ge;80%.
        </p>
        {rcaAccuracy && rcaAccuracy.labeled_count > 0 ? (
          <div className="flex items-baseline gap-4">
            <div className={`text-lg font-bold ${(rcaAccuracy.accuracy_percent ?? 0) >= 80 ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}`}>
              {rcaAccuracy.accuracy_percent}% <span className="text-sm font-normal text-muted-foreground">accuracy</span>
            </div>
            <div className="text-sm text-muted-foreground">
              {rcaAccuracy.confirmed_count} confirmed correct / {rcaAccuracy.labeled_count} labeled
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No causes labeled yet - use the RCA tab's thumbs up/down to start tracking this.</p>
        )}
      </div>

      <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card">
        <h3 className="mb-1.5 text-sm font-semibold text-foreground">RCA Reconciliation (AC-RCA-002)</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          How often the attributed causes' combined contribution reconciles to the observed cost delta within &plusmn;3% -
          computed from the same reconciliation check every RCA run already performs, rolled up here for the first time.
        </p>
        {reconciliation && reconciliation.total_count > 0 ? (
          <div className="flex items-baseline gap-4">
            <div className={`text-lg font-bold ${(reconciliation.within_tolerance_percent ?? 0) >= 80 ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}`}>
              {reconciliation.within_tolerance_percent}% <span className="text-sm font-normal text-muted-foreground">within tolerance (30d)</span>
            </div>
            <div className="text-sm text-muted-foreground">
              {reconciliation.within_tolerance_count} / {reconciliation.total_count} runs
              {reconciliation.avg_deviation_percent != null ? ` · avg deviation ${reconciliation.avg_deviation_percent}pp` : ''}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No RCA runs with a reconciliation result in the last 30 days yet.</p>
        )}
      </div>

      <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card">
        <h3 className="mb-1.5 text-sm font-semibold text-foreground">RCA Usefulness (AC-RCA-003)</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          Real analyst feedback from the "Was this useful?" control at the bottom of each RCA result - the spec's own
          bar is &ge;80%.
        </p>
        {usefulness && usefulness.rated_count > 0 ? (
          <div className="flex items-baseline gap-4">
            <div className={`text-lg font-bold ${(usefulness.useful_percent ?? 0) >= 80 ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}`}>
              {usefulness.useful_percent}% <span className="text-sm font-normal text-muted-foreground">rated useful</span>
            </div>
            <div className="text-sm text-muted-foreground">
              {usefulness.useful_count} / {usefulness.rated_count} ratings
              {usefulness.avg_rating != null ? ` · avg rating ${usefulness.avg_rating}/5` : ''}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No usefulness ratings yet - use the RCA tab's "Was this useful?" control to start tracking this.</p>
        )}
      </div>

      <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card">
        <h3 className="mb-1.5 text-sm font-semibold text-foreground">RCA Grounding - Ungrounded Claims (Req 45.3)</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          Claims from RCA narratives that couldn't be traced back to real evidence even after an automatic stricter
          rewrite - each of these was still shown to you, flagged as low-confidence, not hidden.
        </p>
        {incidents30d !== null && (
          <div className="mb-4 text-base font-semibold tracking-tight text-foreground">
            {incidents30d} <span className="text-sm font-normal text-muted-foreground">incidents in the last 30 days</span>
          </div>
        )}
        {recentIncidents.length > 0 ? (
          <ul className="space-y-2 text-sm">
            {recentIncidents.map((inc, i) => (
              <li key={i} className="rounded border border-border p-2 dark:border-border">
                <div className="text-foreground/80">{inc.claim}</div>
                <div className="text-xs text-muted-foreground/70">{new Date(inc.flagged_at).toLocaleString()} - confidence {Number(inc.evidence_match_score).toFixed(2)}</div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No ungrounded claims recorded yet.</p>
        )}
      </div>
    </div>
  );
};
